import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "../plugins/axios";
import localService from "../services/localService";
import router from "../router";
import { showMessage } from "../utils/notifications";
import { useInvestorAccessStore } from "./investorAccess";

// Helper function for post-login handling
function handlePostLogin(data) {
  const loginType = {
    type: "normal",
    provider: "signup",
  };

  // Update store state
  const authStore = useAuthStore();
  authStore.currentUser = data.person;
  authStore.isLoggedIn = true;

  // Store in localStorage
  localService.setItem("breton-userInfo", {
    ...data.person,
  });
  localService.setItem("breton-token", data.access_token);
  localService.setItem("breton-loginType", loginType);
  // Check once per login whether this user owes an investor access review.
  useInvestorAccessStore().markReviewPending();
  router.push({ path: "/dashboard" });
}

export const useAuthStore = defineStore("auth", () => {
  // State
  const isLoading = ref(false);
  const currentUser = ref({});
  const isLoggedIn = ref(false);
  const loginType = ref({});

  // Getters
  const getCurrentUser = computed(() => currentUser.value);
  const getIsLoading = computed(() => isLoading.value);
  const getIsLoggedIn = computed(() => isLoggedIn.value);

  // Actions
  const setState = (key, value) => {
    switch (key) {
      case "isLoading":
        isLoading.value = value;
        break;
      case "currentUser":
        currentUser.value = value;
        break;
      case "isLoggedIn":
        isLoggedIn.value = value;
        break;
      case "loginType":
        loginType.value = value;
        break;
    }
  };

  const resetState = () => {
    currentUser.value = {};
    isLoggedIn.value = false;
  };

  const startLoading = () => {
    isLoading.value = true;
  };

  const stopLoading = () => {
    isLoading.value = false;
  };

  // Login action
  const login = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post("/login", payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return;
      }

      if (data.person.mfa_enabled) {
        return { showMfa: true };
      }

      handlePostLogin(data);
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  // Logout action
  const logout = async () => {
    try {
      resetState();
      localService.removeItem("breton-token");
      localService.removeItem("breton-userInfo");
      useInvestorAccessStore().clearReviewPending();
      router.push({ path: "/" });
    } catch (e) {
      // Handle error
    }
  };

  // Forgot password action
  const forgotPassword = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post("/reset_password", payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      return data;
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  // Confirm email action
  const confirmEmail = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post(`/verify/${payload.token}/${payload.uidb}`);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return;
      }

      setTimeout(() => {
        currentUser.value = data.user;
        isLoggedIn.value = true;
        localService.setItem("userInfo", {
          ...data.user,
        });
        localService.setItem("token", data.user.access_token);
        router.push({ path: "/dashboard" });
      }, 3000);
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  // Resend email action
  const resendEmail = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post(`/resend_confirmation`, payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  // Reset password action
  const resetPassword = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post(`/reset_password/${payload.token}/${payload.uidb}`, {
        password: payload.password,
      });

      if (!data.success) {
        if (data.message === "Reset Password token has expired") {
          setTimeout(function () {
            window.location = "/reset-password";
          }, 3000);
        }
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      return data;
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  // MFA login action
  const loginMfa = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post("/login_mfa", payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return;
      }

      handlePostLogin(data);
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  // Login using recovery code action
  const loginUsingRecovery = async (payload) => {
    try {
      isLoading.value = true;
      const { data } = await axios.post("/verify_recovery_code", payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return;
      }

      handlePostLogin(data);
    } catch (e) {
      // Handle error
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    currentUser,
    isLoggedIn,
    loginType,

    // Getters
    getCurrentUser,
    getIsLoading,
    getIsLoggedIn,

    // Actions
    setState,
    resetState,
    startLoading,
    stopLoading,
    login,
    logout,
    forgotPassword,
    confirmEmail,
    resendEmail,
    resetPassword,
    loginMfa,
    loginUsingRecovery,
  };
});
